"use client"

import { convertToLocale } from "@lib/util/money"
import React from "react"

type CartTotalsProps = {
  totals: {
    total?: number | null
    subtotal?: number | null
    tax_total?: number | null
    currency_code: string
    item_subtotal?: number | null
    shipping_subtotal?: number | null
    discount_subtotal?: number | null
  }
  itemCount?: number
  /** Cart is still pre-checkout, so £0 shipping/tax means "not yet known" rather than "free/exempt". */
  pendingCalculation?: boolean
  /** Name of the shipping option the shipping amount is based on (estimate or attached method). */
  shippingLabel?: string
}

const CartTotals: React.FC<CartTotalsProps> = ({ totals, itemCount, pendingCalculation, shippingLabel }) => {
  const {
    currency_code,
    total,
    tax_total,
    item_subtotal,
    shipping_subtotal,
    discount_subtotal,
  } = totals

  return (
    <div>
      <div className="flex flex-col gap-y-2 text-sm text-gray-600">
        {typeof itemCount === "number" && (
          <div className="flex items-center justify-between">
            <span>Total Items</span>
            <span data-testid="cart-total-items" data-value={itemCount}>
              {itemCount}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span>Subtotal (excl. shipping and taxes)</span>
          <span data-testid="cart-subtotal" data-value={item_subtotal || 0}>
            {convertToLocale({ amount: item_subtotal ?? 0, currency_code })}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span>Shipping{shippingLabel ? ` (${shippingLabel})` : ""}</span>
          {!shipping_subtotal && pendingCalculation ? (
            <span data-testid="cart-shipping" className="text-gray-500 italic">
              Calculated at checkout
            </span>
          ) : (
            <span data-testid="cart-shipping" data-value={shipping_subtotal || 0}>
              {convertToLocale({ amount: shipping_subtotal ?? 0, currency_code })}
            </span>
          )}
        </div>
        {!!discount_subtotal && (
          <div className="flex items-center justify-between">
            <span>Discount</span>
            <span
              className="text-brand-accent font-semibold"
              data-testid="cart-discount"
              data-value={discount_subtotal || 0}
            >
              -{" "}
              {convertToLocale({
                amount: discount_subtotal ?? 0,
                currency_code,
              })}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="flex gap-x-1 items-center">Taxes</span>
          {!tax_total && pendingCalculation ? (
            <span data-testid="cart-taxes" className="text-gray-500 italic">
              Calculated at checkout
            </span>
          ) : (
            <span data-testid="cart-taxes" data-value={tax_total || 0}>
              {convertToLocale({ amount: tax_total ?? 0, currency_code })}
            </span>
          )}
        </div>
      </div>
      <div className="h-px w-full border-b border-gray-200 my-4" />
      <div className="flex items-center justify-between text-gray-900 mb-2 text-base font-medium">
        <span>Total</span>
        <span
          className="text-xl font-bold text-brand-primary"
          data-testid="cart-total"
          data-value={total || 0}
        >
          {convertToLocale({ amount: total ?? 0, currency_code })}
        </span>
      </div>
      <div className="h-px w-full border-b border-gray-200 mt-4" />
    </div>
  )
}

export default CartTotals
